import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Network, ArrowRight, Database, Waves, Layers, Fingerprint, UserCheck, Filter, Rocket, ChevronDown, Bot, ShieldCheck, Building2, Zap } from "lucide-react";
import { ARCHITECTURE_PIPELINE } from "@/data/architectureData";

const ICONS = {
  database: Database, waves: Waves, layers: Layers, fingerprint: Fingerprint, "user-check": UserCheck,
  filter: Filter, rocket: Rocket, bot: Bot, "shield-check": ShieldCheck, building: Building2, zap: Zap,
};

const ACCENT_TEXT = { cyan: "text-cyan-400", teal: "text-teal-400", fuchsia: "text-fuchsia-400", amber: "text-amber-400", emerald: "text-emerald-400" };

export default function ArchitectureVisualizerPage({
  pipeline = ARCHITECTURE_PIPELINE,
  title = "Data Cloud Architecture",
  subtitle = "Click any stage of the pipeline to see how customer data flows from raw source systems all the way to activation.",
  accent = "cyan",
  testId = "architecture-visualizer-page",
}) {
  const [activeId, setActiveId] = useState(pipeline[0].id);
  const active = pipeline.find((n) => n.id === activeId) || pipeline[0];
  const accentText = ACCENT_TEXT[accent] || ACCENT_TEXT.cyan;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid={testId}>
      <div className="flex items-center gap-3 mb-2">
        <Network className={`w-6 h-6 ${accentText}`} />
        <span className={`text-xs font-bold uppercase tracking-[0.2em] ${accentText}`}>Interactive Explorer</span>
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-white">{title}</h1>
      <p className="text-sm sm:text-base text-slate-400 mt-2 max-w-2xl">{subtitle}</p>

      <div className="mt-10 flex flex-col lg:flex-row items-stretch lg:items-center gap-2 overflow-x-auto pb-2" data-testid="architecture-pipeline">
        {pipeline.map((node, i) => {
          const Icon = ICONS[node.icon] || Database;
          const isActive = node.id === activeId;
          return (
            <React.Fragment key={node.id}>
              <motion.button
                onClick={() => setActiveId(node.id)}
                data-testid={`architecture-node-${node.id}`}
                whileHover={{ y: -3 }}
                className={`shrink-0 lg:flex-1 flex lg:flex-col items-center gap-3 lg:gap-2 rounded-2xl p-4 border transition-all ${
                  isActive ? "border-white/30 bg-white/[0.06]" : "border-white/10 bg-white/[0.02] hover:border-white/20"
                }`}
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${node.color}1a`, boxShadow: isActive ? `0 0 20px ${node.color}40` : "none" }}>
                  <Icon className="w-5 h-5" style={{ color: node.color }} />
                </div>
                <span className={`text-xs font-semibold text-center ${isActive ? "text-white" : "text-slate-400"}`}>{node.title}</span>
              </motion.button>
              {i < pipeline.length - 1 && (
                <ArrowRight className="hidden lg:block w-4 h-4 text-slate-600 shrink-0" />
              )}
              {i < pipeline.length - 1 && (
                <ChevronDown className="lg:hidden w-4 h-4 text-slate-600 shrink-0 mx-auto" />
              )}
            </React.Fragment>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={active.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.25 }}
          className="glass-panel rounded-2xl p-8 mt-8" data-testid="architecture-detail-panel"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${active.color}1a` }}>
              {(() => { const Icon = ICONS[active.icon] || Database; return <Icon className="w-5 h-5" style={{ color: active.color }} />; })()}
            </div>
            <h2 className="font-heading text-xl font-semibold text-white">{active.title}</h2>
          </div>
          <p className="text-sm text-slate-300 mb-4">{active.summary}</p>
          <ul className="space-y-2.5">
            {active.details.map((d) => (
              <li key={d} className="flex items-start gap-2 text-sm text-slate-400 leading-relaxed">
                <span className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ background: active.color }} /> {d}
              </li>
            ))}
          </ul>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
